import { payUiUrl } from "../../lib/credentials";
import { useDeployment } from "../../lib/deployment";
import { bayIds } from "../../lib/evse";
import type { ChargePoint } from "../../lib/types";
import { CopyButton } from "../ui/Copy";
import { CopyQrImage, QrCode } from "../ui/QrCode";
import { MicroLabel, SectionTitle } from "../ui/Text";

/**
 * The codes a driver scans. pay-ui spec §4.2.
 *
 * ## What this replaces
 *
 * Someone pasting `https://pay…/CP-0001/2` into an online QR generator, one socket
 * at a time, and trusting that they typed the connector number that matches the
 * sticker they are about to apply. That is not a hypothetical process — it is the
 * process — and its failure mode is the worst kind: a code that scans perfectly and
 * quotes a price for the wrong socket, discovered when a driver pays to charge a car
 * that is not theirs.
 *
 * Here the addresses are derived from the station this page is already showing, so
 * the id cannot be mistyped and the connector numbers are the ones the station
 * itself reported.
 *
 * ## Per connector, and one for the station
 *
 * A driver scanning a socket should land on that socket — pay-ui routes
 * `/{id}/{connectorId}` straight to a price and a *start* button. The station-level
 * code (`/{id}`) asks them which socket they are at, which is the right code for a
 * charger whose sockets carry no numbers of their own, and the only one available
 * before a station has ever booted and said how many it has.
 *
 * ## Printing is the export; the clipboard is the message
 *
 * The codes are vector (`ui/QrCode`), so the operator's own browser print is how
 * these reach label stock — a download button would be a second thing to maintain
 * that produces a worse artefact than the one the browser already makes. *Copy
 * image* is for the other errand entirely: putting one socket's code into a work
 * order, a supplier's label template, or a message to the engineer standing at the
 * charger, none of which will take a printed page.
 */

export function DriverAccess({ station }: { readonly station: ChargePoint }) {
  const { payUiBaseUrl, isResolving, hasFailed } = useDeployment();

  const stationUrl = payUiUrl({ payUiBaseUrl, chargePointId: station.id });

  /**
   * The bays a driver can pay for, paired with the address of each. One per EVSE,
   * not per gun: a driver buys a bay, and two guns on one EVSE share one sticker
   * (docs/ocpp-201-spec.md D2). On a 1.6 station EVSE N is its connector N.
   *
   * `payUiUrl` is the filter, not a predicate written here: it refuses EVSE 0 —
   * the charge point itself, which a station reports alongside its real bays — so
   * anything it declines is something with no driver-facing page to point a
   * sticker at. Mapping the station's list straight onto codes is how a sticker
   * for EVSE 0 gets printed and lands a driver on a 404.
   */
  const sockets = bayIds(station.connectors)
    .map((evseId) => ({
      evseId,
      url: payUiUrl({ payUiBaseUrl, chargePointId: station.id, evseId }),
    }))
    .filter((socket): socket is { evseId: number; url: string } => socket.url !== undefined);

  if (stationUrl === undefined) {
    /*
      Nothing to draw. Silent while the request is in flight, and otherwise the
      specific reason — a fleet-only deployment has no pay-ui at all, and an
      operator looking for a missing section deserves to know which of the two it is
      rather than being sent to read the source.
    */
    if (isResolving) return null;

    return (
      <section className="flex flex-col gap-4">
        <SectionTitle>Driver access</SectionTitle>
        <p className="m-0 max-w-prose text-data text-ink-muted">
          {hasFailed
            ? "This deployment's public charging address could not be read, so there are no codes to print."
            : null}
          {hasFailed ? null : (
            <>
              This deployment has no public charging address configured, so there are no codes to
              print. Set <span className="font-mono">PUBLIC_APP_BASE_URL</span> on the server to the
              origin a driver's phone reaches pay-ui at.
            </>
          )}
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      {/*
        The sale state is deliberately *not* restated here as a badge or a status
        line. `PublicAccessPanel` sits directly above and says it once, properly;
        a second copy of the same fact on the same screen is how two statements of
        it start disagreeing. What this says instead is the consequence for the
        thing this section is actually about — the sticker.
      */}
      <SectionTitle>Driver access</SectionTitle>

      <p className="m-0 max-w-prose text-data text-ink-muted">
        {station.publicAccess
          ? "What a driver's phone opens after scanning the sticker on each socket. Print this page to put them on label stock."
          : "These addresses resolve, but a driver scanning one today is told the charger cannot be used. Set the price above before the stickers go on."}
      </p>

      {/*
        A grid rather than a row, so a charger with six sockets wraps instead of
        scrolling. `items-start` because a connector whose URL wraps to two lines
        must not drag its neighbours' codes down with it.
      */}
      <ul className="m-0 grid list-none grid-cols-1 items-start gap-x-8 gap-y-6 p-0 md:grid-cols-2 xl:grid-cols-3">
        {sockets.map((socket) => (
          <Sticker key={socket.evseId} label={`EVSE ${socket.evseId}`} url={socket.url} />
        ))}

        {/*
          Last, not first: the per-socket codes are the ones that go on the hardware,
          and this one is the fallback. Its caption says which is which, because two
          near-identical codes side by side with no explanation is how the wrong one
          gets printed.
        */}
        <Sticker
          label="Whole station"
          url={stationUrl}
          hint={
            sockets.length === 0
              ? "This station has not reported a usable socket yet, so this is the only code available. Per-socket codes appear here after its first boot."
              : "Asks the driver which socket they are at. For a charger whose sockets are not numbered."
          }
        />
      </ul>
    </section>
  );
}

function Sticker({
  label,
  url,
  hint,
}: {
  readonly label: string;
  readonly url: string;
  readonly hint?: string;
}) {
  return (
    <li className="flex flex-col gap-3 border-t border-rule pt-3">
      <MicroLabel>{label}</MicroLabel>
      {/*
        The code sits on its own raised surface with a hairline edge — the same
        vocabulary as every other panel — which also gives the printed sticker a cut
        line.
      */}
      {/*
        The code and the control that copies it, as one block. `Copy image` sits
        under the picture rather than beside the address below, because the two
        copies on this sticker take different things — this one the graphic, the one
        below the address as text — and a pair of buttons in a row named `Copy` and
        `Copy image` makes the operator work out which is which.
      */}
      <div className="flex w-fit flex-col items-start gap-2">
        <div className="border border-rule bg-paper-raised p-2">
          <QrCode value={url} label={`${label}: scan to open the driver page`} />
        </div>
        <CopyQrImage value={url} what={`${label} code`} />
      </div>

      {/*
        The address in text under its own code, not just behind a copy button. It is
        how an operator checks that the code they are about to print points at the
        connector the caption claims — the one thing a QR code cannot be read for.
        `MicroLabel` above is the only label here; a second one would name the same
        thing twice.
      */}
      <div className="flex flex-wrap items-start gap-3">
        <code className="min-w-0 flex-1 select-all break-all bg-paper px-2 py-1 font-mono text-micro tracking-normal normal-case text-ink">
          {url}
        </code>
        <CopyButton value={url} what={`${label} address`} />
      </div>

      {hint === undefined ? null : (
        <p className="m-0 text-micro tracking-normal normal-case text-ink-faint">{hint}</p>
      )}
    </li>
  );
}
